import { useParams, Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Calendar, Tag, Clock, User, Send, CheckCircle2, ArrowRight } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { useBlogPost, useBlogPosts } from "@/data/hooks";
import Seo from "@/components/seo/Seo";
import useRecaptcha from "@/hooks/useRecaptcha";
import Honeypot from "@/components/Honeypot";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SidebarForm({ postTitle }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", phone: "", role: "Owner", referral_source: "Blog" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const recaptcha = useRecaptcha();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.company) { toast.error("Please fill required fields."); return; }
    setLoading(true);
    try {
      const token = await recaptcha.execute("blog_sidebar_submit");
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...form,
        other_info: `Blog: ${postTitle}`,
        source_page: typeof window !== "undefined" ? window.location.pathname : "",
        recaptcha_token: token,
        website_url: honeypot,
      });
      setDone(true);
      toast.success("Request submitted!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
      <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
      <h3 className="text-lg font-bold text-slate-900">Thank You!</h3>
      <p className="text-sm text-slate-500 mt-2">We'll be in touch shortly.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg">
      <Honeypot value={honeypot} onChange={setHoneypot} name="website_url_blog" />
      <div className="text-center mb-5">
        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-3">
          <Send className="h-5 w-5 text-sky-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900">Request a Demo</h3>
        <p className="text-xs text-slate-500 mt-1">See Antera in action for your business</p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-600">First Name *</Label>
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="John" className="mt-1 h-9 text-sm border-slate-300" required />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Last Name</Label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Doe" className="mt-1 h-9 text-sm border-slate-300" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Work Email *</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@company.com" className="mt-1 h-9 text-sm border-slate-300" required />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Company *</Label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Corp" className="mt-1 h-9 text-sm border-slate-300" required />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1 h-9 text-sm border-slate-300" />
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full mt-5 bg-sky-600 hover:bg-sky-700 text-white rounded-full h-11 text-sm font-semibold">
        {loading ? "Submitting..." : "Schedule Your Personalized Demo"}
      </Button>
      <p className="text-[10px] text-slate-400 text-center mt-3">No commitment required.</p>
    </form>
  );
}

function RelatedPosts({ currentSlug, category, posts }) {
  const related = posts.filter(p => p.slug !== currentSlug && p.category === category).slice(0, 3);
  if (!related.length) return null;

  return (
    <div className="mt-10 pt-8 border-t border-slate-200">
      <h3 className="text-lg font-bold text-slate-900 mb-5">Related Articles</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {related.map(p => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="group block border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
            {p.image && <div className="h-28 overflow-hidden bg-slate-100"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>}
            <div className="p-3">
              <h4 className="text-sm font-semibold text-slate-900 line-clamp-2 group-hover:text-sky-600 transition-colors leading-snug">{p.title}</h4>
              <p className="text-xs text-slate-400 mt-1">{p.date}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { post, loading } = useBlogPost(slug);
  const { posts: allPosts } = useBlogPosts();
  const fullContent = post?.bodyHtml || "";
  // Sanitize HTML once per post to prevent XSS while still allowing rich markup.
  // We keep the safe defaults but explicitly forbid scripts and dangerous handlers.
  const sanitizedContent = useMemo(() => {
    if (!fullContent) return "";
    return DOMPurify.sanitize(fullContent, {
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "style"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
    });
  }, [fullContent]);

  if (loading) {
    return (
      <div className="page-transition pt-32 pb-16 lg:pt-40 lg:pb-24 min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">Loading article…</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="page-transition pt-32 pb-16 lg:pt-40 lg:pb-24 min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Post Not Found</h1>
          <p className="mt-4 text-slate-500">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="inline-block mt-6">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="page-transition">
      <Seo
        title={`${post.title} | Antera Insights`}
        description={post.excerpt || `${post.title} - Antera Software blog post.`}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          author: { "@type": "Person", name: post.author || "Antera Software" },
          datePublished: post.date,
          image: post.image,
          publisher: {
            "@type": "Organization",
            name: "Antera Software",
            logo: { "@type": "ImageObject", url: "/api/media/adff924b-451a-45d0-a130-026ae966ce9a" },
          },
        }}
      />
      <Toaster position="top-right" richColors />

      {/* Full-width header */}
      <section className="pt-28 pb-8 lg:pt-36 lg:pb-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 mb-6">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Antera Insights
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 bg-sky-50 px-2.5 py-1 rounded-full">
                <Tag className="h-3 w-3" /> {post.category}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                <Calendar className="h-3 w-3" /> {post.date}
              </span>
              {post.readTime && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="h-3 w-3" /> {post.readTime}
                </span>
              )}
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight max-w-3xl">
              {post.title}
            </h1>

            {post.author && (
              <div className="mt-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 text-xs font-bold">
                  {post.author.split(" ").map(n => n[0]).join("")}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{post.author}</p>
                  <p className="text-xs text-slate-400">Antera Software</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content + Sidebar */}
      <section className="pb-12 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Left - Article content */}
            <div className="lg:col-span-2">
              {post.image && (
                <div className="rounded-xl overflow-hidden border border-slate-200 mb-8">
                  <img src={post.image} alt={post.title} className="w-full h-auto" />
                </div>
              )}

              {sanitizedContent ? (
                <div
                  className="blog-article-content"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
              ) : (
                <div className="blog-article-content">
                  <p>{post.excerpt}</p>
                </div>
              )}

              {/* Related posts */}
              <RelatedPosts currentSlug={slug} category={post.category} posts={allPosts} />

              {/* Bottom CTAs - mobile */}
              <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col sm:flex-row gap-3 lg:hidden">
                <Link to="/contact">
                  <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 text-sm font-semibold">Request a Demo</Button>
                </Link>
                <Link to="/blog">
                  <Button variant="outline" className="rounded-full px-6 text-sm font-semibold border-slate-300">More Articles</Button>
                </Link>
              </div>
            </div>

            {/* Right - Sticky sidebar */}
            <div className="hidden lg:block relative">
              <div className="lg:sticky lg:top-24 space-y-5">
                <SidebarForm postTitle={post.title} />

                {/* Quick stats */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Why Antera</p>
                  <div className="space-y-3">
                    {[
                      { stat: "18+ Years", label: "In the Promo Industry" },
                      { stat: "Cloud-Based", label: "ERP, CRM & eCommerce" },
                      { stat: "QuickBooks", label: "& PromoStandards Integrated" },
                    ].map((s) => (
                      <div key={s.label} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-slate-600">{s.label}</span>
                        <span className="text-sm font-bold text-sky-600 shrink-0">{s.stat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* More articles link */}
                <Link to="/blog" className="block text-center text-sm font-medium text-sky-600 hover:text-sky-700 py-2">
                  View All Articles <ArrowRight className="inline h-3.5 w-3.5 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
